/**
 * Zod-based query parameter schemas for the explore tool.
 *
 * This is the single source of truth for:
 *   - TypeScript types (derived via z.infer<>)
 *   - Default values (encoded in Zod .default())
 *   - Runtime validation & coercion (URL strings → typed values)
 *   - Serialization (typed values → URL strings, omitting defaults)
 */

import { z } from "zod";
import {
  ProductClass,
  ProductLevel,
  TradeDirection,
  TradeFlow,
  LocationDetailLevel,
  ServicesClass,
  UIView,
  VizType,
  ColorBy,
  OverTimeLayoutOption,
  OverTimeOrderingOption,
  YAxisScalingOption,
  HideWorldTradeOption,
  OpportunitySizeOption,
  NodeSizing,
} from "../types";
import { worldGroupDatum } from "../graphql/queries/getLocationsMetadata";
import { allProductsDatum } from "../graphql/queries/getProductsMetadata";
import { generateRandomCountrySelection } from "../utils/locationUtils";

// ── Zod enum schemas ──────────────────────────────────────────────

const UIViewSchema = z.nativeEnum(UIView);
const ProductClassSchema = z.nativeEnum(ProductClass);
const TradeDirectionSchema = z.nativeEnum(TradeDirection);
const TradeFlowSchema = z.nativeEnum(TradeFlow);
const ProductLevelSchema = z.nativeEnum(ProductLevel);
const LocationDetailLevelSchema = z.nativeEnum(LocationDetailLevel);
const ServicesClassSchema = z.nativeEnum(ServicesClass);
const ColorBySchema = z.nativeEnum(ColorBy);
const OverTimeLayoutSchema = z.nativeEnum(OverTimeLayoutOption);
const OverTimeOrderingSchema = z.nativeEnum(OverTimeOrderingOption);
const YAxisScalingSchema = z.nativeEnum(YAxisScalingOption);
const HideWorldTradeSchema = z.nativeEnum(HideWorldTradeOption);
const NodeSizingSchema = z.nativeEnum(NodeSizing);
const OpportunitySizeSchema = z.nativeEnum(OpportunitySizeOption);

// ── Coercion helpers ──────────────────────────────────────────────

/**
 * Coerces a URL string to number, treating empty string and non-numeric
 * values as null. Matches the behavior of use-query-params NumberParam.
 */
const coerceNullableNumber = z.preprocess((val) => {
  if (val === "" || val === undefined || val === null) return null;
  const n = Number(val);
  return Number.isNaN(n) ? null : n;
}, z.number().nullable());

/**
 * Coerces a URL string to number with a non-null default.
 * Empty string / missing → uses the Zod default.
 */
const coerceNumber = z.preprocess((val) => {
  if (val === "" || val === undefined || val === null) return undefined;
  const n = Number(val);
  return Number.isNaN(n) ? undefined : n;
}, z.number());

// ── Default country (evaluated once at module load) ───────────────

const defaultRandomCountry = generateRandomCountrySelection();

// ── Base schema (shared across all viz types) ─────────────────────

const baseParamSchema = z.object({
  exporter: z.string().default(defaultRandomCountry),
  importer: z.string().default(worldGroupDatum.groupId),
  view: UIViewSchema.default(UIView.Products),
  year: coerceNullableNumber.default(2024),
  startYear: coerceNullableNumber.optional(),
  endYear: coerceNullableNumber.optional(),
  tradeDirection: TradeDirectionSchema.default(TradeDirection.Exports),
  productClass: ProductClassSchema.default(ProductClass.HS92Products),
  product: z.string().default(allProductsDatum.productId),
});

/** Type for the base params shared across all viz types. */
export type BaseExploreParams = z.infer<typeof baseParamSchema>;

// ── Per-viz schemas ───────────────────────────────────────────────

export const treemapParamSchema = baseParamSchema.extend({
  servicesClass: ServicesClassSchema.default(ServicesClass.unilateral),
  productLevel: coerceNumber.pipe(ProductLevelSchema).default(ProductLevel.Product4digit),
  locationLevel: LocationDetailLevelSchema.default(LocationDetailLevel.country),
  tradeFlow: TradeFlowSchema.default(TradeFlow.Gross),
  colorBy: ColorBySchema.default(ColorBy.Sector),
});

export const geoParamSchema = baseParamSchema.extend({
  // Override base default: Geomap is always Markets view
  view: UIViewSchema.default(UIView.Markets),
  productLevel: coerceNumber.pipe(ProductLevelSchema).default(ProductLevel.ProductSection),
  tradeFlow: TradeFlowSchema.default(TradeFlow.Gross),
});

export const overtimeParamSchema = baseParamSchema.extend({
  startYear: coerceNullableNumber.default(1995),
  endYear: coerceNullableNumber.default(2024),
  servicesClass: ServicesClassSchema.default(ServicesClass.unilateral),
  productLevel: coerceNumber.pipe(ProductLevelSchema).default(ProductLevel.ProductSection),
  locationLevel: LocationDetailLevelSchema.default(LocationDetailLevel.region),
  tradeFlow: TradeFlowSchema.default(TradeFlow.Gross),
  layout: OverTimeLayoutSchema.default(OverTimeLayoutOption.Value),
  ordering: OverTimeOrderingSchema.default(OverTimeOrderingOption.Community),
  yAxis: YAxisScalingSchema.default(YAxisScalingOption.Current),
});

export const marketshareParamSchema = baseParamSchema.extend({
  startYear: coerceNullableNumber.default(1995),
  endYear: coerceNullableNumber.default(2024),
  servicesClass: ServicesClassSchema.default(ServicesClass.unilateral),
  productLevel: coerceNumber.pipe(ProductLevelSchema).default(ProductLevel.ProductSection),
  tradeFlow: TradeFlowSchema.default(TradeFlow.Gross),
});

export const feasibilityParamSchema = baseParamSchema.extend({
  productClass: ProductClassSchema.default(ProductClass.HS92Products),
  productLevel: coerceNumber.pipe(ProductLevelSchema).default(ProductLevel.Product4digit),
  hideExports: HideWorldTradeSchema.default(HideWorldTradeOption.On),
  sizing: NodeSizingSchema.or(OpportunitySizeSchema).default(NodeSizing.WorldTrade),
  yAxis: z.string().default("pci"),
});

export const productspaceParamSchema = baseParamSchema.extend({
  productClass: ProductClassSchema.default(ProductClass.HS92Products),
  productLevel: coerceNumber.pipe(ProductLevelSchema).default(ProductLevel.Product4digit),
  tradeFlow: TradeFlowSchema.default(TradeFlow.Gross),
  colorBy: ColorBySchema.default(ColorBy.Cluster),
  sizing: NodeSizingSchema.or(OpportunitySizeSchema).default(NodeSizing.WorldTrade),
  rcaThreshold: coerceNumber.default(1),
});

// ── Schema registry ───────────────────────────────────────────────

export const vizParamSchemas = {
  [VizType.Tree]: treemapParamSchema,
  [VizType.Geo]: geoParamSchema,
  [VizType.OverTime]: overtimeParamSchema,
  [VizType.MarketShare]: marketshareParamSchema,
  [VizType.Feasibility]: feasibilityParamSchema,
  [VizType.ProductSpace]: productspaceParamSchema,
} as const;

// ── Derived TypeScript types ──────────────────────────────────────

export type TreeParams = z.infer<typeof treemapParamSchema>;
export type GeoParams = z.infer<typeof geoParamSchema>;
export type OverTimeParams = z.infer<typeof overtimeParamSchema>;
export type MarketShareParams = z.infer<typeof marketshareParamSchema>;
export type FeasibilityParams = z.infer<typeof feasibilityParamSchema>;
export type ProductSpaceParams = z.infer<typeof productspaceParamSchema>;

/** Maps VizType to its narrowed param interface. */
export type VizParamMap = {
  [VizType.Tree]: TreeParams;
  [VizType.Geo]: GeoParams;
  [VizType.OverTime]: OverTimeParams;
  [VizType.MarketShare]: MarketShareParams;
  [VizType.Feasibility]: FeasibilityParams;
  [VizType.ProductSpace]: ProductSpaceParams;
};

/**
 * Wide type covering all possible param keys across viz types.
 * Makes all per-viz-specific keys optional so callers can access any
 * key without narrowing the viz type.
 */
export type DecodedExploreParams =
  & z.infer<typeof baseParamSchema>
  & Partial<Omit<TreeParams, keyof z.infer<typeof baseParamSchema>>>
  & Partial<Omit<GeoParams, keyof z.infer<typeof baseParamSchema>>>
  & Partial<Omit<OverTimeParams, keyof z.infer<typeof baseParamSchema>>>
  & Partial<Omit<MarketShareParams, keyof z.infer<typeof baseParamSchema>>>
  & Partial<Omit<FeasibilityParams, keyof z.infer<typeof baseParamSchema>>>
  & Partial<Omit<ProductSpaceParams, keyof z.infer<typeof baseParamSchema>>>;

/** Union of all possible param keys across all viz types. */
export type ExploreParamKey =
  | keyof TreeParams
  | keyof GeoParams
  | keyof OverTimeParams
  | keyof MarketShareParams
  | keyof FeasibilityParams
  | keyof ProductSpaceParams;

// ── Utilities ─────────────────────────────────────────────────────

/**
 * Parse URL search params for a given viz type.
 *
 * Coerces string values, validates enums, and applies defaults for
 * missing/invalid params. Uses safeParse so invalid values fall back
 * to schema defaults rather than throwing.
 */
export function parseSearchParams<V extends VizType>(
  vizType: V,
  searchParams: URLSearchParams,
): VizParamMap[V] {
  const raw = Object.fromEntries(searchParams.entries());
  const schema = vizParamSchemas[vizType];
  const result = schema.safeParse(raw);
  if (result.success) {
    return result.data as VizParamMap[V];
  }
  // Per-field fallback: parse each field individually so one invalid
  // param doesn't discard valid ones (e.g. a bad colorBy shouldn't
  // lose the exporter/importer).
  const defaults = schema.parse({}) as Record<string, unknown>;
  const shape = schema.shape as Record<string, z.ZodTypeAny>;
  const merged: Record<string, unknown> = { ...defaults };
  for (const [key, fieldSchema] of Object.entries(shape)) {
    if (key in raw) {
      const fieldResult = fieldSchema.safeParse(raw[key]);
      if (fieldResult.success) {
        merged[key] = fieldResult.data;
      }
    }
  }
  return merged as VizParamMap[V];
}

/**
 * Get the default values for a given viz type.
 */
export function getDefaults<V extends VizType>(vizType: V): VizParamMap[V] {
  return vizParamSchemas[vizType].parse({}) as VizParamMap[V];
}

/**
 * Serialize typed params to URLSearchParams, omitting values that match
 * the schema defaults.
 */
// Params that must always be serialized to the URL regardless of whether
// they match the current schema default. The exporter default is random
// per page load, so omitting it produces unreliable shared URLs.
const alwaysSerialize = new Set<string>(["exporter"]);

export function serializeParams<V extends VizType>(
  vizType: V,
  params: VizParamMap[V],
): URLSearchParams {
  const defaults = getDefaults(vizType);
  const searchParams = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;
    const defaultValue = (defaults as Record<string, unknown>)[key];
    if (alwaysSerialize.has(key) || String(value) !== String(defaultValue)) {
      searchParams.set(key, String(value));
    }
  }

  return searchParams;
}

// ── Param reset & merge utilities ─────────────────────────────────

/** Params to reset (delete from URL) when switching to each viz type. */
export const resetParamsPerViz: Record<VizType, ExploreParamKey[]> = {
  [VizType.Tree]: ["colorBy"],
  [VizType.Geo]: ["view", "productLevel"],
  [VizType.OverTime]: ["yAxis"],
  [VizType.MarketShare]: ["view", "productLevel", "tradeFlow"],
  [VizType.Feasibility]: ["view", "productLevel", "yAxis"],
  [VizType.ProductSpace]: ["view", "productLevel", "productClass"],
};

/** Primary selections preserved when resetting settings. */
export const primarySelections: ExploreParamKey[] = [
  "exporter",
  "importer",
  "view",
  "year",
  "startYear",
  "endYear",
  "product",
  "tradeDirection",
];

/**
 * Merge current URL params for a target viz type, filtering out defaults
 * and resetting viz-specific params. Used when switching between viz types.
 *
 * Returns a URL search string (without leading `?`).
 */
export function mergeParams(
  vizType: VizType,
  params: Record<string, string>,
): string {
  const dontFilterEmptyString =
    vizType === VizType.Tree ||
    vizType === VizType.OverTime ||
    vizType === VizType.Geo;

  // Normalize empty strings → world group ID (except for some viz types)
  const augmentedParams = Object.fromEntries(
    Object.entries(params).map(([key, value]) => {
      if (dontFilterEmptyString && value === "") return [key, value];
      if (value === "") return [key, worldGroupDatum.groupId];
      return [key, value];
    }),
  );

  // Filter to only params that differ from defaults for this viz type.
  // Zod omits optional keys (e.g. startYear/endYear) from parse({}) when unset,
  // so `key in defaults` is false for those — still carry them when present in
  // the URL so subroute navigation preserves the user's year range for viz types
  // that do not use it in the UI.
  const defaults = getDefaults(vizType) as Record<string, unknown>;
  const sharedParams: Record<string, string> = {};
  for (const [key, value] of Object.entries(augmentedParams)) {
    const inDefaults = key in defaults;
    const differsFromDefault =
      inDefaults && String(defaults[key]) !== String(value);
    const carryUnusedPrimary =
      !inDefaults &&
      primarySelections.includes(key as ExploreParamKey) &&
      value !== "";
    if (differsFromDefault || carryUnusedPrimary) {
      sharedParams[key] = value;
    }
  }

  // Remove params that should reset when switching to this viz type
  for (const key of resetParamsPerViz[vizType]) {
    delete sharedParams[key];
  }

  // Geomap constraint: one location must be world
  if (vizType === VizType.Geo) {
    const geoDefaults = getDefaults(VizType.Geo);
    const exp = sharedParams.exporter || geoDefaults.exporter;
    const imp = sharedParams.importer || geoDefaults.importer;
    if (exp !== worldGroupDatum.groupId && imp !== worldGroupDatum.groupId) {
      delete sharedParams.importer;
      sharedParams.tradeDirection = TradeDirection.Exports;
    }
  }

  return new URLSearchParams(sharedParams).toString();
}
